import React from 'react';
import { Form, message, Tabs } from 'antd';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { Button } from 'src/components';
import FormModalStyle from './FormModalStyle';
import { useAppDispatch, useAppSelector } from 'src/stores';
import { InputField, TextAreaField, UploadImage } from 'src/components/form';
import { COMPONENT_VALUES, LIST_LANGUAGES } from 'src/constants/form';
import { upperCaseFirstWord } from 'src/utils/string';
import {
  createPageContentAction,
  updatePageContentAction,
} from 'src/stores/screens/pageContent/pageContent.action';
import classNames from 'classnames';

interface FormModalProps {
  isModalFormOpen: boolean;
  handleCancel: () => void;
  handleOk: () => void;
  sessionId?: string;
  currentRecord?: PageContent.PageContentType | null;
  fetchData: (page?: number) => void;
  currentPage?: number;
}

const FormModal: React.FC<FormModalProps> = ({
  isModalFormOpen,
  handleCancel,
  handleOk,
  sessionId,
  currentRecord,
  fetchData,
  currentPage,
}) => {
  const dispatch = useAppDispatch();
  const { isLoading } = useAppSelector((state) => state.page);
  const { session } = useAppSelector((state) => state.pageSession);

  const component = session?.component

  const validationSchema = yup.object().shape({
    sessionId: yup.string().required('Session ID is required'),
    fileUrl: yup
      .string()
      .when('$component', {
        is: () => component === COMPONENT_VALUES.CAROUSEL,
        then: (schema) => schema.required('Image is required'),
        otherwise: (schema) => schema.notRequired(),
      }),
    titleVi: yup
      .string()
      .nullable()
      .trim()
      .max(100, 'Title must be at most 100 characters')
      .when('$component', {
        is: () => component !== COMPONENT_VALUES.CAROUSEL,
        then: (schema) => schema.required('Title is required'),
        otherwise: (schema) => schema.notRequired(),
      }),
    descriptionVi: yup.string().nullable().trim()
      .max(2000, 'Description must be at most 2000 characters'),
    titleEn: yup
      .string()
      .nullable()
      .trim()
      .max(100, 'Title must be at most 100 characters')
      .when('$component', {
        is: () => component !== COMPONENT_VALUES.CAROUSEL,
        then: (schema) => schema.required('Title is required'),
        otherwise: (schema) => schema.notRequired(),
      }),
    descriptionEn: yup.string().nullable().trim()
      .max(2000, 'Description must be at most 2000 characters'),
    titleJa: yup
      .string()
      .nullable()
      .trim()
      .max(100, 'Title must be at most 100 characters')
      .when('$component', {
        is: () => component !== COMPONENT_VALUES.CAROUSEL,
        then: (schema) => schema.required('Title is required'),
        otherwise: (schema) => schema.notRequired(),
      }),
    descriptionJa: yup.string().nullable().trim()
      .max(2000, 'Description must be at most 2000 characters'),
    titleKo: yup
      .string()
      .nullable()
      .trim()
      .max(100, 'Title must be at most 100 characters')
      .when('$component', {
        is: () => component !== COMPONENT_VALUES.CAROUSEL,
        then: (schema) => schema.required('Title is required'),
        otherwise: (schema) => schema.notRequired(),
      }),
    descriptionKo: yup.string().nullable().trim()
      .max(2000, 'Description must be at most 2000 characters'),
  });

  const initialValues: PageContent.PageContentPostDetailParams =
    currentRecord?.id
      ? { ...currentRecord, sessionId }
      : {
          sessionId,
          titleVi: '',
          titleEn: '',
          titleJa: '',
          titleKo: '',
          descriptionVi: '',
          descriptionEn: '',
          descriptionJa: '',
          descriptionKo: '',
          fileUrl: '',
        };

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema,
    initialValues,
    onSubmit: async (values: PageContent.PageContentPostDetailParams) => {
      const result: any = currentRecord?.id
        ? await dispatch(
          updatePageContentAction({
            data: { ...values, id: currentRecord.id, sessionId },
          })
        )
        : await dispatch(
          createPageContentAction({ data: { ...values, sessionId } })
        );
      if (result?.payload?.error) {
        message.error(result?.payload?.message);
      } else {
        message.success(
          currentRecord?.id ? 'Update successful' : 'Creation successful'
        );
        formik.resetForm({});
        fetchData(currentRecord?.id ? currentPage : 1);
        handleOk();
      }
    },
  });

  const onClose = () => {
    formik.resetForm({});
    handleCancel();
  };

  const renderListTabItems = LIST_LANGUAGES.map((item) => {
    const fieldNameTitle = 'title' + upperCaseFirstWord(item);
    const fieldNameDescription = 'description' + upperCaseFirstWord(item);

    const isErrors = formik.errors[fieldNameTitle] || formik.errors[fieldNameDescription]
    return (
      <Tabs.TabPane
        tabKey={item}
        tab={
          <span className={classNames({ error: isErrors })}>
            {item.toUpperCase()}
            {component !== COMPONENT_VALUES.CAROUSEL && (
              <span style={{ color: 'red' }}> *</span>
            )}
          </span>
        }
        key={item}
      >
        <InputField
          className="form-control"
          label="Title"
          field={formik.getFieldProps(fieldNameTitle)}
          setFieldValue={formik.setFieldValue}
          inputProps={{
            size: 'middle',
            placeholder: 'Title',
          }}
          error={formik.errors[fieldNameTitle]}
          touched={formik.touched[fieldNameTitle]}
          required={component !== COMPONENT_VALUES.CAROUSEL}
        />
        <TextAreaField
          className="form-control"
          label="Description"
          field={formik.getFieldProps(fieldNameDescription)}
          setFieldValue={formik.setFieldValue}
          textAreaProps={{
            size: 'middle',
            placeholder: 'Description',
            autoSize: { minRows: 4, maxRows: 10 }
          }}
          error={formik.errors[fieldNameDescription]}
          touched={formik.touched[fieldNameDescription]}
        />
      </Tabs.TabPane>
    );
  });

  return (
    <FormModalStyle
      title={currentRecord?.id ? 'Update' : 'Create'}
      visible={isModalFormOpen}
      onCancel={onClose}
      className="modal-form"
      width={800}
    >
      <Form
        name="edit-form"
        className="setting-form"
        layout="vertical"
        onFinish={() => {
          formik.handleSubmit();
        }}
      >
        <UploadImage
          className="form-control"
          label="Image (Recommend 16:9)"
          field={formik.getFieldProps('fileUrl')}
          file={formik.getFieldProps('fileUrl').value}
          fileType={formik.values.fileType}
          setFieldValue={formik.setFieldValue}
          error={formik.errors.fileUrl}
          touched={formik.touched.fileUrl}
          required={component === COMPONENT_VALUES.CAROUSEL}
        />

        <hr />

        <Tabs defaultActiveKey="en">{renderListTabItems}</Tabs>
        {/* Button footer */}
        <div className="button-footer">
          <Button buttonType="cancel" onClick={onClose} width={100}>
            Close
          </Button>
          <Button htmlType="submit" width={100} disabled={isLoading}>
            {currentRecord?.id ? 'Update' : 'Create'}
          </Button>
        </div>
      </Form>
    </FormModalStyle>
  );
};

export default FormModal;
